import {
  put,
  call,
  all,
  takeEvery,
  select,
  takeLatest,
} from 'redux-saga/effects';
import { actions as streamActions } from '@wix/da-shared-react/pkg/Stream';
import history from '@wix/da-shared-react/pkg/redux/routing/history';
import { Url } from '@wix/da-url';
import {
  setViewType,
  unsetSelectedFolderId,
  unsetCurrentlyViewedFolderId,
  setSelectedFolderId,
  clearSearch,
  pauseCurrentFolderDeviationFetching,
  setFullViewFolderIds,
  setSearchQuery,
  setSearchInputValue,
  setCurrentlyViewedFolderId,
  setSortOrder,
  setSelectedSubfolderId,
  unsetSelectedSubfolderId,
} from '../actions/gallectionSection';
import { SectionViewType } from '../../types/sections';
import {
  getSearchInputValue,
  getGallectionSectionType,
  getSortOrder,
  getSearchQuery,
  getCurrentlyViewedFolder,
  getCurrentlyViewedFolderId,
  getSelectedSubfolder,
} from '../selectors/gallectionSection';
import {
  STREAM_PARAM_TYPE_FETCHING_PAUSED,
  getFolderDeviationsStreamId,
} from '../streams/folderDeviations';
import { getDefaultFolder } from '../selectors/folders';
import { getProfileOwnerUser } from '../selectors/users';
import { isSectionLoading } from '../selectors/sections';
import { DEFAULT_GALLECTION_SORT_ORDER } from '../components/Sections/GallectionsSection/consts';
import { pageViewHandler, logSearchSubmit } from './biLogger';

export function* updateURLSaga() {
  if (typeof window === 'undefined') {
    return;
  }
  const isLoading = yield select(isSectionLoading);
  if (isLoading) {
    return;
  }
  const user = yield select(getProfileOwnerUser);
  const defaultFolder = yield select(getDefaultFolder);
  const gallectionType = yield select(getGallectionSectionType);
  const currentlyViewedFolder = yield select(getCurrentlyViewedFolder);
  const selectedSubfolder = yield select(getSelectedSubfolder);
  const searchQuery = yield select(getSearchQuery);
  const sortOrder = yield select(getSortOrder);

  const currentFolderOrSubfolder = selectedSubfolder || currentlyViewedFolder;

  const includeFolderInURL =
    currentlyViewedFolder &&
    currentlyViewedFolder.folderId !== defaultFolder.folderId;

  const includeSortOrder = sortOrder !== DEFAULT_GALLECTION_SORT_ORDER;

  const newUrl = Url.userGalleryPath({
    username: user.username,
    gallectionType,
    folderId: includeFolderInURL
      ? currentFolderOrSubfolder.folderId
      : undefined,
    folderName: includeFolderInURL ? currentFolderOrSubfolder.name : undefined,
    searchQuery,
    sortOrder: includeSortOrder ? sortOrder : undefined,
  });

  history.push(newUrl);
}

export function* setViewTypeSaga(action: ReturnType<typeof setViewType>) {
  if (action.payload === SectionViewType.GRID) {
    yield all([
      put(unsetSelectedFolderId()),
      put(unsetCurrentlyViewedFolderId()),
      put(setSearchQuery('')),
      put(setSearchInputValue('')),
    ]);
  }
}

export function* setSelectedFolderIdSaga(
  action: ReturnType<typeof setSelectedFolderId>
) {
  const searchInputValue = yield select(getSearchInputValue);
  if (searchInputValue) {
    yield all([put(setSearchQuery('')), put(setSearchInputValue(''))]);
  }
  yield all([
    put(setFullViewFolderIds([action.payload])),
    put(setCurrentlyViewedFolderId(action.payload)),
    put(unsetSelectedSubfolderId()),
  ]);
}

export function* unsetSelectedFolderIdSaga(
  action: ReturnType<typeof unsetSelectedFolderId>
) {
  yield put(setFullViewFolderIds([]));
}

export function* setSearchQuerySaga(action: ReturnType<typeof setSearchQuery>) {
  if (action.payload) {
    // if not empty search query
    yield all([
      put(unsetSelectedFolderId()),
      put(unsetCurrentlyViewedFolderId()),
      call(logPageViewIfNotLoading),
      call(logSearchSubmit, action.payload),
    ]);
  }
}

export function* pauseCurrentFolderDeviationFetchingSaga(
  action: ReturnType<typeof pauseCurrentFolderDeviationFetching>
) {
  const currentlyViewedFolderId = yield select(getCurrentlyViewedFolderId);
  const gallectionSectionType = yield select(getGallectionSectionType);
  const sortOrder = yield select(getSortOrder);
  const streamId = getFolderDeviationsStreamId(
    gallectionSectionType,
    currentlyViewedFolderId,
    sortOrder
  );
  yield put(
    streamActions.setParam({
      streamId,
      paramType: STREAM_PARAM_TYPE_FETCHING_PAUSED,
      paramValue: true,
    })
  );
}

export function* clearSearchSaga(action: ReturnType<typeof clearSearch>) {
  const defaultFolder = yield select(getDefaultFolder);
  yield all([
    put(setSearchQuery('')),
    put(setSearchInputValue('')),
    put(setSelectedFolderId(defaultFolder.folderId)),
  ]);
}

export function* logPageViewIfNotLoading() {
  const isLoading = yield select(isSectionLoading);
  if (isLoading) {
    return;
  }
  yield call(pageViewHandler);
}

export default [
  takeEvery(setViewType, setViewTypeSaga),
  takeEvery(setSelectedFolderId, setSelectedFolderIdSaga),
  takeEvery(unsetSelectedFolderId, unsetSelectedFolderIdSaga),
  takeEvery(setSearchQuery, setSearchQuerySaga),
  takeEvery(clearSearch, clearSearchSaga),
  takeLatest(
    [
      unsetSelectedFolderId,
      setCurrentlyViewedFolderId,
      setSelectedSubfolderId,
      setSearchQuery,
      setSortOrder,
    ],
    updateURLSaga
  ),
  takeEvery(
    pauseCurrentFolderDeviationFetching,
    pauseCurrentFolderDeviationFetchingSaga
  ),
  takeLatest(
    [setCurrentlyViewedFolderId, setSelectedSubfolderId, setViewType],
    logPageViewIfNotLoading
  ),
];
